import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Upload, X, Image as ImageIcon, Video, CheckCircle2, Film } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let _idCounter = 0;

const isVideoUrl = (url = '') => /\.(mp4|mov|avi|mkv|webm|wmv|flv|m4v|3gp|ogv)$/i.test(url);
const isImageUrl = (url = '') => /\.(jpg|jpeg|png|gif|webp|bmp|svg|heic)$/i.test(url) || url.includes('/api/cms-image/');

export const ImageUploader = ({
  onImageUploaded,
  multiple        = false,
  initialImageUrl = '',
  label           = 'Click to upload image or video',
}) => {
  const { getAuthHeader } = useAuth();
  const [uploading,    setUploading]    = useState(false);
  const [uploadedUrls, setUploadedUrls] = useState([]);
  const [preview,      setPreview]      = useState(initialImageUrl || '');
  const [progress,     setProgress]     = useState({ current: 0, total: 0 });
  const [dragOver,     setDragOver]     = useState(false);

  const inputId = useRef(`media-upload-${++_idCounter}`).current;

  const uploadFiles = async (files) => {
    if (files.length === 0) return;
    setUploading(true);
    setProgress({ current: 0, total: files.length });
    const urls = [];
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setProgress({ current: i + 1, total: files.length });
        if (!file.type.startsWith('image/') && !file.type.startsWith('video/') && !file.type.startsWith('application/')) {
          toast.error(`${file.name}: unsupported format`); continue;
        }
        const formData = new FormData();
        formData.append('file', file);
        const response = await axios.post(`${API}/upload-image`, formData, {
          headers: (() => { const crmToken = localStorage.getItem('crm_token'); return crmToken ? { Authorization: `Bearer ${crmToken}` } : { ...getAuthHeader() }; })(),
        });
        urls.push(response.data.url);
        if (!multiple) setPreview(response.data.url);
        const isVid = file.type.startsWith('video/');
        if (files.length > 1) toast.success(`${file.name} uploaded (${i + 1}/${files.length})`);
        else toast.success(`${isVid ? 'Video' : 'Image'} uploaded successfully`);
      }
      setUploadedUrls(prev => [...prev, ...urls]);
      if (multiple) onImageUploaded(urls);
      else if (urls[0]) onImageUploaded(urls[0]);
      if (files.length > 1 && urls.length > 0) toast.success(`${urls.length} file${urls.length > 1 ? 's' : ''} added!`);
    } catch {
      toast.error('Upload failed — please try again.');
    } finally {
      setUploading(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  const handleFileChange = async (e) => {
    await uploadFiles(Array.from(e.target.files));
    e.target.value = '';
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;
    await uploadFiles(files);
  };

  const removeUploaded = (index) => setUploadedUrls(prev => prev.filter((_, i) => i !== index));

  const previewIsVideo = isVideoUrl(preview);
  const showBulkProgress = uploading && progress.total > 1;

  return (
    <div className="space-y-3">
      {/* Single-mode preview */}
      {!multiple && preview && (
        <div className="relative group w-full">
          {previewIsVideo ? (
            <video src={preview} className="w-full h-36 object-cover rounded-lg border border-stone-200" controls muted />
          ) : (
            <img src={preview} alt="Current" className="w-full h-36 object-cover rounded-lg border border-stone-200" />
          )}
          <button
            type="button"
            onClick={() => { setPreview(''); onImageUploaded(''); }}
            className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            title="Remove"
          >
            <X className="w-3.5 h-3.5" />
          </button>
          <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs py-1 text-center rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity">
            {previewIsVideo ? 'Video preview' : 'Click X to remove'} · Upload below to replace
          </div>
        </div>
      )}

      {/* Upload area */}
      <div
        className={`border-2 border-dashed rounded-lg p-5 text-center transition-colors cursor-pointer ${
          dragOver ? 'border-emerald-500 bg-emerald-50' : 'border-stone-300 hover:border-emerald-700'
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept="image/*,video/*"
          multiple={multiple}
          onChange={handleFileChange}
          className="hidden"
          id={inputId}
          disabled={uploading}
        />
        <label htmlFor={inputId} className="cursor-pointer flex flex-col items-center gap-2">
          {uploading ? (
            <div className="flex flex-col items-center gap-1">
              <div className="animate-spin"><Upload className="w-7 h-7 text-emerald-700" /></div>
              {showBulkProgress && (
                <div className="w-full max-w-[160px] mt-1">
                  <div className="w-full bg-stone-200 rounded-full h-1.5">
                    <div
                      className="bg-emerald-600 h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${(progress.current / progress.total) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          ) : dragOver ? (
            <ImageIcon className="w-7 h-7 text-emerald-600" />
          ) : (
            <div className="flex items-center gap-2 text-stone-400">
              <ImageIcon className="w-6 h-6" />
              <span className="text-stone-300 text-lg">|</span>
              <Film className="w-6 h-6" />
            </div>
          )}
          <div>
            <p className="text-sm font-medium text-stone-700">
              {uploading
                ? showBulkProgress
                  ? `Uploading ${progress.current} of ${progress.total}...`
                  : 'Uploading...'
                : dragOver
                  ? 'Drop files here'
                  : label}
            </p>
            <p className="text-xs text-stone-400 mt-0.5">
              {multiple
                ? 'Images & Videos · Drag & drop or click · Ctrl+Click for multiple'
                : 'Images & Videos · Drag & drop or click · All formats supported'}
            </p>
          </div>
        </label>
      </div>

      {/* Multi-mode: newly uploaded preview grid */}
      {multiple && uploadedUrls.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            <span className="text-xs font-medium text-emerald-700">
              {uploadedUrls.length} file{uploadedUrls.length > 1 ? 's' : ''} added this session
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {uploadedUrls.map((url, i) => (
              <div key={i} className="relative group">
                {isVideoUrl(url) ? (
                  <div className="w-full h-24 rounded border border-emerald-200 bg-stone-900 flex items-center justify-center">
                    <Video className="w-8 h-8 text-stone-400" />
                  </div>
                ) : (
                  <img src={url} alt={`Uploaded ${i + 1}`} className="w-full h-24 object-cover border border-emerald-200 rounded" />
                )}
                <div className="absolute top-1 left-1 bg-emerald-500 rounded-full p-0.5">
                  <CheckCircle2 className="w-2.5 h-2.5 text-white" />
                </div>
                <button
                  type="button"
                  onClick={() => removeUploaded(i)}
                  className="absolute top-1 right-1 bg-red-500 text-white p-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
